/**
 * Module: Carousel
 * Child blocks:
 * - Button block
 * - Heading with Paragraph block
 * - list block
 */

/**
 * External dependencies
 */

/**
 * DWT dependencies
 */

import {
  getInnerBlocks,
  passAttrsToInnerBlocks
} from '../utils/block-utils';

import ListInspectorControls from '../block-components/ListInspectorControls';

/**
 * WP dependencies
 */

import {
  InnerBlocks,
  InspectorControls,
  useBlockProps,
  useInnerBlocksProps,
  BlockControls
} from '@wordpress/block-editor';

import {
  PanelBody,
  SelectControl,
  ToggleControl,
  ToolbarButton
} from '@wordpress/components';

import { __ } from '@wordpress/i18n';

const Edit = ({ attributes, setAttributes, clientId }) => {
  const {
    isCarousel,
    featureBy,
    filterBy,
    divisionCode,
    imprintCode,
    seriesCode,
    bisacCode,
    brandCode,
    showMetadata,
    showCovers,
    filterByOptions,
    layout,
    horizontalRule,
    hasCTASlide,
    linkTargetBlank
  } = attributes;

  /**
   * General settings/styles
   */

  const obj = {
    isCarousel,
    featureBy,
    filterBy,
    divisionCode,
    imprintCode,
    seriesCode,
    bisacCode,
    brandCode,
    showMetadata,
    showCovers,
    filterByOptions,
    hasCTASlide
  };

  passAttrsToInnerBlocks( clientId, obj );

  /**
   * Block settings/styles
   */

  const blockProps = useBlockProps({
    className: `block__carousel`
  });

  /**
   * innerBlocks settings/styles
   */

  const innerBlocks = getInnerBlocks( clientId );
  const template = [
    [ 'core/group', {}, [
      [ 'dwt/heading-with-paragraph', {} ],
      [ 'dwt/button', {} ]
    ] ],
    [ 'dwt/product-list', {} ]
  ];

  // eslint-disable-next-line no-unused-vars
  const { children, ...innerBlocksProps } = useInnerBlocksProps(
    { className: `inner-blocks-wrapper relative ${layout}` },
    {
      allowedBlocks: [],
      renderAppender: false,
      template
    }
  );

  let booklistInnerBlocks = [];
  let ctaSlide = [];

  Object.entries( innerBlocks ).forEach( ( [ key, value ] ) => {
    if ( value.name === 'dwt/product-list' ) {
      ctaSlide = value.attributes.ctaSlideText;
      booklistInnerBlocks = value.innerBlocks;
    }
  });

  /**
   * handle layout
   */

  const handleLayout = ( newLayout ) => setAttributes({ layout: newLayout });

  return (
    <>
      <BlockControls>
        <ToolbarButton
          className={ linkTargetBlank ? 'is-pressed' : ''}
          icon='external'
          label='Open links in new tab'
          onClick={() => setAttributes({ linkTargetBlank: !linkTargetBlank })}
        />
      </BlockControls>
      <InspectorControls>
        <PanelBody title={ __( 'Main settings', 'dwt-custom-theme' ) }>
          <ListInspectorControls
            setAttributes={ setAttributes }
            isCarousel={ isCarousel }
            featureBy={ featureBy }
            filterBy={ filterBy }
            divisionCode={ divisionCode }
            imprintCode={ imprintCode }
            seriesCode={ seriesCode }
            bisacCode={ bisacCode }
            brandCode={ brandCode }
            showMetadata={ showMetadata }
            showCovers={ showCovers }
            filterByOptions={ filterByOptions }
            innerBlocks={ booklistInnerBlocks }
          />
        </PanelBody>
        <PanelBody title={ __( 'Styling settings', 'dwt-custom-theme' ) }>
          <SelectControl
            label={__( 'Layout', 'dwt-custom-theme' ) }
            value={ layout }
            options={ [
              { label: 'Left Aligned', value: 'is-left-aligned' },
              { label: 'Center Aligned', value: 'is-center-aligned' }
            ] }
            onChange={ handleLayout }
            __nextHasNoMarginBottom
          />
          <ToggleControl
            className="editor-toggle-button"
            label={ __( 'Divider', 'dwt-custom-theme' ) }
            help={__( 'Displays a thin line at the bottom of the module' ) }
            checked={ horizontalRule }
            onChange={ () => {
              setAttributes({ horizontalRule: !horizontalRule });
            } }
          />
          <ToggleControl
            className="editor-toggle-button"
            label={ __( 'Carousel CTA', 'dwt-custom-theme' ) }
            help={__( 'Displays a CTA at the end of the carousel' ) }
            checked={ hasCTASlide }
            onChange={ () => {
              setAttributes({ hasCTASlide: !hasCTASlide });
              ctaSlide.length = 0;
            } }
          />
        </PanelBody>

      </InspectorControls>

      <div { ...blockProps }>
        <div className='carousel--inner'>
          <div { ...innerBlocksProps }>
            <InnerBlocks
              template={ template }
              templateLock="all"
            />
          </div>
        </div>
      </div>

      { horizontalRule && (
        <hr style={ { border: '1px solid' } } />
      ) }
    </>
  );
};

export default Edit;
